import React, { useEffect, useMemo, useRef, useState } from "react"
import languagesRaw from "./data.json"

type Language = { code: string; name: string }

type LanguagesSelectProps = {
  value: string
  onChange: (nextValue: string) => void

  placeholder?: string
  searchPlaceholder?: string

  disabled?: boolean
  className?: string

  searchInputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
}

function splitNames(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
}

function joinNames(names: string[]): string {
  return names.join(", ")
}

function uniqPreserveOrder(arr: string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const x of arr) {
    if (!seen.has(x)) {
      seen.add(x)
      out.push(x)
    }
  }
  return out
}

export function LanguagesSelect({
  value,
  onChange,
  placeholder = "Select languages",
  searchPlaceholder = "Search",
  disabled = false,
  className = "",
  searchInputProps,
}: LanguagesSelectProps) {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)

  const all = useMemo<Language[]>(() => {
    if (!Array.isArray(languagesRaw)) return []
    return (languagesRaw as unknown[]).filter((x): x is Language => {
      if (!x || typeof x !== "object") return false
      const anyX = x as any
      return typeof anyX.code === "string" && typeof anyX.name === "string"
    })
  }, [])

  const byName = useMemo(() => {
    const m = new Map<string, Language>()
    for (const l of all) m.set(l.name, l)
    return m
  }, [all])

  const selectedNames = useMemo(() => {
    return splitNames(value).filter((name) => byName.has(name))
  }, [value, byName])

  const selectedSet = useMemo(() => new Set(selectedNames), [selectedNames])

  const filtered = useMemo(() => {
    const q = normalize(query)
    if (!q) return all
    return all.filter((l) => normalize(`${l.name} ${l.code}`).includes(q))
  }, [all, query])

  useEffect(() => {
    if (activeIndex >= filtered.length)
      setActiveIndex(Math.max(0, filtered.length - 1))
  }, [filtered.length, activeIndex])

  const close = () => {
    setOpen(false)
    setQuery("")
    requestAnimationFrame(() => buttonRef.current?.focus())
  }

  const toggleName = (name: string) => {
    if (disabled) return
    const next = selectedSet.has(name)
      ? selectedNames.filter((n) => n !== name)
      : uniqPreserveOrder([...selectedNames, name])
    onChange(joinNames(next))
  }

  useEffect(() => {
    if (!open) return
    const onMouseDown = (e: MouseEvent) => {
      const root = rootRef.current
      if (root && !root.contains(e.target as Node)) close()
    }
    document.addEventListener("mousedown", onMouseDown)
    return () => document.removeEventListener("mousedown", onMouseDown)
  }, [open])

  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open) return
    const container = listRef.current
    const el = container?.querySelector<HTMLElement>(
      `[data-item-index="${activeIndex}"]`,
    )
    if (!container || !el) return

    const top = el.offsetTop
    const bottom = top + el.offsetHeight
    if (top < container.scrollTop) container.scrollTop = top
    else if (bottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = bottom - container.clientHeight
    }
  }, [open, activeIndex])

  const onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Escape") {
      e.preventDefault()
      close()
      return
    }
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((i) => Math.max(i - 1, 0))
      return
    }
    if (e.key === "Enter") {
      e.preventDefault()
      const item = filtered[activeIndex]
      if (item) toggleName(item.name)
    }
  }

  return (
    <div
      ref={rootRef}
      className={`w-full ${className}`}
      data-languages-select-root
    >
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => {
            if (disabled) return
            setOpen((v) => !v)
            setActiveIndex(0)
          }}
          className={[
            "w-full border border-0 bg-white px-3 py-2 h-[44px] text-left text-black shadow-sm",
            disabled ? "cursor-not-allowed opacity-60" : "hover:bg-slate-50",
            "rounded-none outline-none focus:outline-none",
          ].join(" ")}
        >
          <div className="flex items-center justify-between gap-3">
            <span
              className={`min-w-0 flex-1 truncate ${selectedNames.length ? "" : "text-gray-200"}`}
            >
              {selectedNames.length ? joinNames(selectedNames) : placeholder}
            </span>

            <svg
              className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08Z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </button>

        {open ? (
          <div className="absolute z-50 bottom-full mb-2 w-full border border-gray-200 bg-white shadow-lg rounded-none">
            <div className="p-2">
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setActiveIndex(0)
                }}
                onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder}
                {...searchInputProps}
                className={[
                  "w-full border border-gray-150 bg-white px-3 py-2 text-base text-black placeholder-gray-200",
                  "rounded-none outline-none focus:outline-none",
                  searchInputProps?.className ?? "",
                ].join(" ")}
              />
            </div>

            <div
              ref={listRef}
              role="listbox"
              aria-multiselectable="true"
              className="max-h-64 overflow-auto p-2 pt-0"
            >
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-base text-gray-300">
                  No results
                </div>
              ) : (
                filtered.map((l, idx) => {
                  const checked = selectedSet.has(l.name)
                  const active = idx === activeIndex

                  return (
                    <button
                      key={`${l.code}-${l.name}`}
                      type="button"
                      role="option"
                      aria-selected={checked}
                      data-item-index={idx}
                      onClick={() => toggleName(l.name)}
                      className={[
                        "flex w-full items-center justify-between gap-3 border border-transparent px-3 py-2 text-base text-slate-900",
                        "rounded-none outline-none focus:outline-none",
                        active ? "bg-slate-100" : "hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <div className="flex min-w-0 shrink-0 items-center gap-3">
                        <span
                          aria-hidden="true"
                          className="inline-flex h-4 w-4 items-center justify-center border border-slate-400 bg-white rounded-none"
                        >
                          {checked ? (
                            <svg
                              viewBox="0 0 20 20"
                              className="h-3 w-3"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.01 7.06a1 1 0 0 1-1.423-.006L3.29 8.77a1 1 0 1 1 1.414-1.414l3.268 3.268 6.3-6.34a1 1 0 0 1 1.432.006Z"
                                clipRule="evenodd"
                              />
                            </svg>
                          ) : null}
                        </span>

                        <span className="truncate">
                          {l.name}{" "}
                          <span className="text-slate-500">({l.code})</span>
                        </span>
                      </div>

                      {checked ? (
                        <span className="text-gray-200">Selected</span>
                      ) : null}
                    </button>
                  )
                })
              )}
            </div>

            <div className="border-t border-gray-150 p-2 text-right">
              <button
                type="button"
                onClick={() => {
                  if (disabled) return
                  onChange("")
                  setQuery("")
                  setActiveIndex(0)
                }}
                className="text-xs font-sans-bold uppercase bg-white px-2 py-1 text-sm text-gray-300 hover:bg-slate-50 rounded-none outline-none focus:outline-none"
              >
                Clear
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
